/**
 * ComparisonSliderEditor - Editor component for Comparison Slider Block
 *
 * Features:
 * - Title with H1/H2/H3 level selector
 * - Description with rich text editing
 * - Block-level content type selector (Text + image / Text only / Video)
 * - Two-column panel layout (Option 1 / Option 2)
 * - Collapsed label fields per panel
 * - Content type controls which fields appear in both panels simultaneously
 * - Image selection via ImageInsertModal
 * - Video selection via VideoSelectorModal
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/core';
import type {
  ComparisonSliderBlock,
  ComparisonSliderContentType,
  ComparisonSliderImageSize,
  ComparisonSliderOption,
  ComparisonSliderTitleLevel,
} from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';
import { createContentBlockStyles } from '@course-authoring/ui-blocks';

import { ComparisonSliderFieldEditor } from './ComparisonSliderFieldEditor';
import { ComparisonSliderOptionSection } from './ComparisonSliderOptionSection';
import {
  comparisonSliderEditorInnerStyles,
  createComparisonSliderEditorStyles,
} from './comparisonSliderEditorStyles';
import type {
  ComparisonSliderFieldName,
  ComparisonSliderOptionKey,
} from './comparisonSliderEditorTypes';
import { ImageInsertModal, type ImageInsertData } from './ImageInsertModal';
import {
  VideoSelectorModal,
  type VideoSelectionResult,
  type CurrentVideoData,
} from './VideoSelectorModal';
import { useImageInsertTarget } from './useImageInsertTarget';
import { TitleLevelSelector, getTitleTypography } from './TitleLevelSelector';

const COMPARISON_SLIDER_IMAGE_ASPECT_RATIO = '1:1';

const CONTENT_TYPE_OPTIONS: Array<{
  value: ComparisonSliderContentType;
  label: string;
}> = [
  { value: 'text-image', label: 'Text + image' },
  { value: 'text-only', label: 'Text only' },
  { value: 'video', label: 'Video' },
];

export interface ComparisonSliderEditorProps {
  block: ComparisonSliderBlock;
  onChange: (updates: Partial<ComparisonSliderBlock>) => void;
  autoFocus?: boolean;
  translationMode?: boolean;
}

function createOptionUpdate(
  block: ComparisonSliderBlock,
  option: ComparisonSliderOptionKey,
  updates: Partial<ComparisonSliderOption>
): Partial<ComparisonSliderBlock> {
  if (option === 'option1') {
    return {
      option1: {
        ...block.option1,
        ...updates,
      },
    };
  }

  return {
    option2: {
      ...block.option2,
      ...updates,
    },
  };
}

export function ComparisonSliderEditor({
  block,
  onChange,
  autoFocus = false,
  translationMode = false,
}: ComparisonSliderEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const [focusedField, setFocusedField] = useState<ComparisonSliderFieldName | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    target: imageModalTarget,
    isOpen: isImageModalOpen,
    openTarget: openImageModalTarget,
    clearTarget: clearImageModalTarget,
  } = useImageInsertTarget<ComparisonSliderOptionKey>();
  const [videoModalTarget, setVideoModalTarget] =
    useState<ComparisonSliderOptionKey | null>(null);

  const contentType = block.contentType || 'text-image';
  const titleLevel = block.titleLevel || 'h2';

  const isFocused = focusedField !== null;
  const editorStyles = useMemo(() => {
    const baseStyles = createContentBlockStyles(tokens);
    return createComparisonSliderEditorStyles(tokens, baseStyles, isFocused);
  }, [isFocused, tokens]);

  const titleTypography = useMemo(
    () => getTitleTypography(titleLevel, tokens),
    [titleLevel, tokens]
  );

  const titleEditorStyle = useMemo(
    () => ({
      ...editorStyles.titleEditor,
      fontSize: titleTypography.fontSize,
      fontWeight: titleTypography.fontWeight,
      lineHeight: titleTypography.lineHeight,
    }),
    [editorStyles.titleEditor, titleTypography]
  );

  // ── Field change handlers ──────────────────────────────────────────

  const handleFieldChange = useCallback(
    (fieldName: ComparisonSliderFieldName, content: JSONContent) => {
      switch (fieldName) {
        case 'title':
          onChange({ title: content });
          return;
        case 'subtitle':
          onChange({ subtitle: content });
          return;
        case 'option1.title':
          onChange(createOptionUpdate(block, 'option1', { title: content }));
          return;
        case 'option1.body':
          onChange(createOptionUpdate(block, 'option1', { body: content }));
          return;
        case 'option2.title':
          onChange(createOptionUpdate(block, 'option2', { title: content }));
          return;
        case 'option2.body':
          onChange(createOptionUpdate(block, 'option2', { body: content }));
          return;
      }
    },
    [block, onChange]
  );

  const handleTitleLevelChange = useCallback(
    (level: ComparisonSliderTitleLevel) => {
      onChange({ titleLevel: level });
    },
    [onChange]
  );

  const handleContentTypeChange = useCallback(
    (newContentType: ComparisonSliderContentType) => {
      onChange({ contentType: newContentType });
    },
    [onChange]
  );

  const handleImageSizeChange = useCallback(
    (newImageSize: ComparisonSliderImageSize) => {
      onChange({ imageSize: newImageSize });
    },
    [onChange]
  );

  const handleCollapsedLabelChange = useCallback(
    (option: ComparisonSliderOptionKey, value: string) => {
      onChange(
        createOptionUpdate(block, option, {
          collapsedLabel: value || undefined,
        })
      );
    },
    [block, onChange]
  );

  // ── Image handlers ─────────────────────────────────────────────────

  const handleImageAltChange = useCallback(
    (option: ComparisonSliderOptionKey, imageAlt: string) => {
      onChange(
        createOptionUpdate(block, option, {
          imageAlt: imageAlt || undefined,
        })
      );
    },
    [block, onChange]
  );

  const openImageModal = useCallback((option: ComparisonSliderOptionKey) => {
    openImageModalTarget(option);
  }, [openImageModalTarget]);

  const closeImageModal = useCallback(() => {
    clearImageModalTarget();
  }, [clearImageModalTarget]);

  const imageModalInitialImage = useMemo(() => {
    if (!imageModalTarget) return undefined;
    const option = imageModalTarget === 'option1' ? block.option1 : block.option2;
    if (!option.imageUrl) return undefined;
    return { src: option.imageUrl, alt: option.imageAlt };
  }, [block.option1, block.option2, imageModalTarget]);

  const handleImageInsert = useCallback(
    (data: ImageInsertData) => {
      if (!imageModalTarget) return;
      if (!data.src) {
        onChange(
          createOptionUpdate(block, imageModalTarget, {
            imageUrl: undefined,
            imageAlt: undefined,
            imageLibraryItemId: undefined,
          })
        );
        clearImageModalTarget();
        return;
      }

      const trimmedAlt = data.alt.trim();
      const safeAlt = trimmedAlt.length >= 5 ? trimmedAlt : 'Image';
      onChange(
        createOptionUpdate(block, imageModalTarget, {
          imageUrl: data.src,
          imageAlt: safeAlt,
          imageLibraryItemId: data.libraryItemId,
        })
      );
      clearImageModalTarget();
    },
    [block, clearImageModalTarget, imageModalTarget, onChange]
  );

  const handleRemoveImage = useCallback(
    (option: ComparisonSliderOptionKey) => {
      onChange(
        createOptionUpdate(block, option, {
          imageUrl: undefined,
          imageAlt: undefined,
          imageLibraryItemId: undefined,
        })
      );
    },
    [block, onChange]
  );

  // ── Video handlers ─────────────────────────────────────────────────

  const openVideoModal = useCallback((option: ComparisonSliderOptionKey) => {
    setVideoModalTarget(option);
  }, []);

  const closeVideoModal = useCallback(() => {
    setVideoModalTarget(null);
  }, []);

  const currentVideoData = useMemo((): CurrentVideoData | undefined => {
    if (!videoModalTarget) return undefined;
    const option = videoModalTarget === 'option1' ? block.option1 : block.option2;
    if (!option.videoUrl) return undefined;
    return {
      url: option.videoUrl,
      posterUrl: option.videoPosterUrl,
      centerstageVideoId: option.centerstageVideoId,
      title: option.videoTitle,
    };
  }, [block.option1, block.option2, videoModalTarget]);

  const handleVideoSelect = useCallback(
    (data: VideoSelectionResult) => {
      if (!videoModalTarget) return;
      onChange(
        createOptionUpdate(block, videoModalTarget, {
          videoUrl: data.url,
          videoPosterUrl: data.posterUrl || undefined,
          centerstageVideoId: data.centerstageVideoId || undefined,
          videoTitle: data.title || undefined,
        })
      );
      setVideoModalTarget(null);
    },
    [block, videoModalTarget, onChange]
  );

  const handleVideoRemove = useCallback(() => {
    if (!videoModalTarget) return;
    onChange(
      createOptionUpdate(block, videoModalTarget, {
        videoUrl: undefined,
        videoPosterUrl: undefined,
        centerstageVideoId: undefined,
        videoTitle: undefined,
        videoShortDescription: undefined,
      })
    );
    setVideoModalTarget(null);
  }, [block, videoModalTarget, onChange]);

  const handleRemoveVideo = useCallback(
    (option: ComparisonSliderOptionKey) => {
      onChange(
        createOptionUpdate(block, option, {
          videoUrl: undefined,
          videoPosterUrl: undefined,
          centerstageVideoId: undefined,
          videoTitle: undefined,
          videoShortDescription: undefined,
        })
      );
    },
    [block, onChange]
  );

  const handleVideoMetadataChange = useCallback(
    (
      option: ComparisonSliderOptionKey,
      updates: { videoTitle?: string; videoShortDescription?: string }
    ) => {
      onChange(createOptionUpdate(block, option, updates));
    },
    [block, onChange]
  );

  // ── Focus handlers ─────────────────────────────────────────────────

  const handleFieldFocus = useCallback((fieldName: ComparisonSliderFieldName) => {
    setFocusedField(fieldName);
  }, []);

  const handleFieldBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  return (
    <div ref={containerRef} style={editorStyles.container}>
      <style>{comparisonSliderEditorInnerStyles}</style>
      <div style={editorStyles.header}>
        {/* Title section with level selector */}
        {!translationMode ? <TitleLevelSelector
          currentLevel={titleLevel}
          onSelectLevel={(level) => handleTitleLevelChange(level as ComparisonSliderTitleLevel)}
          tokens={tokens}
        /> : null}
        <div>
          <ComparisonSliderFieldEditor
          content={block.title}
          placeholder="Title"
          isFocused={focusedField === 'title'}
          onFocus={() => handleFieldFocus('title')}
          onBlur={handleFieldBlur}
          onContentChange={(content) => handleFieldChange('title', content)}
          autoFocus={autoFocus}
          containerRef={containerRef}
          editorStyle={titleEditorStyle}
          hideToolbar
          />
        </div>

        {/* Description */}
        <div style={{ ...editorStyles.sectionLabel, marginTop: tokens.spacing.lg }}>
          Description
        </div>
        <div>
          <ComparisonSliderFieldEditor
          content={block.subtitle}
          placeholder="Description..."
          allowLists={true}
          isFocused={focusedField === 'subtitle'}
          onFocus={() => handleFieldFocus('subtitle')}
          onBlur={handleFieldBlur}
          onContentChange={(content) => handleFieldChange('subtitle', content)}
          autoFocus={false}
          containerRef={containerRef}
          editorStyle={editorStyles.subtitleEditor}
          />
        </div>
      </div>

      {/* Divider */}
      <hr style={editorStyles.divider} />

      {/* Content type selector */}
      {!translationMode ? <>
      <div style={editorStyles.sectionLabel}>Content</div>
      <div style={editorStyles.contentTypeRow}>
        {CONTENT_TYPE_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleContentTypeChange(option.value)}
            style={{
              ...editorStyles.contentTypeButton,
              ...(contentType === option.value
                ? editorStyles.contentTypeButtonActive
                : editorStyles.contentTypeButtonInactive),
            }}
            aria-pressed={contentType === option.value}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Image size selector (only for text-image content type) */}
      {contentType === 'text-image' && (
        <div style={editorStyles.imageSizeSection}>
          <span style={editorStyles.sectionLabel}>Image Size:</span>
          <button
            type="button"
            style={{
              ...editorStyles.contentTypeButton,
              ...((block.imageSize ?? 'small') === 'small'
                ? editorStyles.contentTypeButtonActive
                : editorStyles.contentTypeButtonInactive),
            }}
            onClick={() => handleImageSizeChange('small')}
          >
            Small
          </button>
          <button
            type="button"
            style={{
              ...editorStyles.contentTypeButton,
              ...((block.imageSize ?? 'small') === 'large'
                ? editorStyles.contentTypeButtonActive
                : editorStyles.contentTypeButtonInactive),
            }}
            onClick={() => handleImageSizeChange('large')}
          >
            Large
          </button>
        </div>
      )}
      </> : null}

      {/* Two-column option panels */}
      <div style={editorStyles.optionsLayout}>
        <ComparisonSliderOptionSection
          optionKey="option1"
          optionLabel="Option 1"
          otherOptionLabel="Option 2"
          option={block.option1}
          contentType={contentType}
          focusedField={focusedField}
          onFieldFocus={handleFieldFocus}
          onFieldBlur={handleFieldBlur}
          onFieldChange={handleFieldChange}
          onCollapsedLabelChange={handleCollapsedLabelChange}
          onImageAltChange={handleImageAltChange}
          onOpenImageModal={openImageModal}
          onRemoveImage={handleRemoveImage}
          onOpenVideoModal={openVideoModal}
          onRemoveVideo={handleRemoveVideo}
          onVideoMetadataChange={handleVideoMetadataChange}
          containerRef={containerRef}
          styles={editorStyles}
          translationMode={translationMode}
        />
        <ComparisonSliderOptionSection
          optionKey="option2"
          optionLabel="Option 2"
          otherOptionLabel="Option 1"
          option={block.option2}
          contentType={contentType}
          focusedField={focusedField}
          onFieldFocus={handleFieldFocus}
          onFieldBlur={handleFieldBlur}
          onFieldChange={handleFieldChange}
          onCollapsedLabelChange={handleCollapsedLabelChange}
          onImageAltChange={handleImageAltChange}
          onOpenImageModal={openImageModal}
          onRemoveImage={handleRemoveImage}
          onOpenVideoModal={openVideoModal}
          onRemoveVideo={handleRemoveVideo}
          onVideoMetadataChange={handleVideoMetadataChange}
          containerRef={containerRef}
          styles={editorStyles}
          translationMode={translationMode}
        />
      </div>

      {/* Image modal */}
      {!translationMode ? <ImageInsertModal
        isOpen={isImageModalOpen}
        onClose={closeImageModal}
        onInsert={handleImageInsert}
        targetAspectRatio={COMPARISON_SLIDER_IMAGE_ASPECT_RATIO}
        initialImage={imageModalInitialImage}
      /> : null}

      {/* Video modal */}
      {!translationMode ? <VideoSelectorModal
        isOpen={videoModalTarget !== null}
        onClose={closeVideoModal}
        onSelect={handleVideoSelect}
        onRemove={handleVideoRemove}
        currentVideo={currentVideoData}
      /> : null}
    </div>
  );
}

ComparisonSliderEditor.displayName = 'ComparisonSliderEditor';
